import { useState, useEffect } from 'react'
import { z } from 'zod'
import { useDashboardStore } from '@/store/dashboard'
import { apiFetch, apiPatch, apiPost, apiDelete, apiPut } from '@/lib/api'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import {
  AI_MODELS,
  ENABLED_MODELS,
  MODEL_CREDIT_COST,
  MODEL_LABELS,
  PROMPT_MAX_LENGTH,
} from '@ekorank/shared'

const OkSchema = z.object({ ok: z.literal(true) })

const SettingsSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    brand: z.object({
      id: z.string(),
      name: z.string(),
      url: z.string().nullable(),
      industry: z.string(),
    }),
    keywords: z.array(z.object({
      id: z.string(),
      promptText: z.string(),
      active: z.boolean(),
    })),
    models: z.array(z.string()),
    creditsBalance: z.number(),
  }),
})

const AddPromptSchema = z.object({
  ok: z.literal(true),
  data: z.object({ id: z.string(), promptText: z.string() }),
})

type Settings = z.infer<typeof SettingsSchema>['data']

interface Props {
  brandId: string
  onDeleted?: () => void
}

export function BrandSettingsContent({ brandId, onDeleted }: Props) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [loading, setLoading] = useState(true)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [industry, setIndustry] = useState('')
  const [savingInfo, setSavingInfo] = useState(false)

  const [newPrompt, setNewPrompt] = useState('')
  const [addingPrompt, setAddingPrompt] = useState(false)

  const [selectedModels, setSelectedModels] = useState<string[]>([])
  const [savingModels, setSavingModels] = useState(false)

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const fetchDashboard = useDashboardStore((s) => s.fetchDashboard)

  useEffect(() => {
    setLoading(true)
    void apiFetch(`/api/brands/${brandId}/settings`, SettingsSchema).then((r) => {
      setLoading(false)
      if (r.ok) {
        const d = r.data.data
        setSettings(d)
        setName(d.brand.name)
        setUrl(d.brand.url ?? '')
        setIndustry(d.brand.industry)
        setSelectedModels(d.models)
      }
    })
  }, [brandId])

  const showFeedback = (type: 'success' | 'error', msg: string) => {
    setFeedback({ type, msg })
    setTimeout(() => setFeedback(null), 3500)
  }

  const handleSaveBrandInfo = async () => {
    setSavingInfo(true)
    const result = await apiPatch(`/api/brands/${brandId}`, OkSchema, {
      name: name.trim(),
      url: url.trim() || null,
      industry: industry.trim(),
    })
    setSavingInfo(false)
    if (result.ok) {
      setSettings((prev) => prev ? { ...prev, brand: { ...prev.brand, name: name.trim(), url: url.trim() || null, industry: industry.trim() } } : prev)
      showFeedback('success', 'Brand info saved.')
    } else {
      showFeedback('error', result.error)
    }
  }

  const handleAddPrompt = async () => {
    if (!newPrompt.trim() || newPrompt.trim().length < 10) return
    setAddingPrompt(true)
    const result = await apiPost(`/api/brands/${brandId}/prompts`, AddPromptSchema, { promptText: newPrompt.trim() })
    setAddingPrompt(false)
    if (result.ok) {
      setNewPrompt('')
      setSettings((prev) => prev ? {
        ...prev,
        keywords: [...prev.keywords, { id: result.data.data.id, promptText: result.data.data.promptText, active: true }],
      } : prev)
    } else {
      showFeedback('error', result.error)
    }
  }

  const handleRemovePrompt = async (promptId: string) => {
    const result = await apiDelete(`/api/brands/${brandId}/prompts/${promptId}`, OkSchema)
    if (result.ok) {
      setSettings((prev) => prev ? {
        ...prev,
        keywords: prev.keywords.filter((k) => k.id !== promptId),
      } : prev)
    } else {
      showFeedback('error', result.error)
    }
  }

  const handleSaveModels = async () => {
    setSavingModels(true)
    const result = await apiPut(`/api/brands/${brandId}/models`, OkSchema, { models: selectedModels })
    setSavingModels(false)
    if (result.ok) {
      setSettings((prev) => prev ? { ...prev, models: selectedModels } : prev)
      showFeedback('success', 'AI models saved.')
    } else {
      showFeedback('error', result.error)
    }
  }

  const handleDeleteBrand = async () => {
    setDeleting(true)
    const result = await apiDelete(`/api/brands/${brandId}`, OkSchema)
    setDeleting(false)
    if (!result.ok) {
      showFeedback('error', result.error)
      setConfirmDelete(false)
      return
    }
    await fetchDashboard()
    onDeleted?.()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-error text-sm">Brand not found or access denied.</p>
      </div>
    )
  }

  const activePrompts = settings.keywords.filter((k) => k.active)

  return (
    <div className="space-y-6">
      {feedback && (
        <div className={`p-4 rounded-lg border text-sm ${
          feedback.type === 'success'
            ? 'bg-secondary/10 border-secondary/30 text-secondary'
            : 'bg-error/10 border-error/30 text-error'
        }`}>
          {feedback.msg}
        </div>
      )}

      {/* Brand Info */}
      <section className="bg-surface-container rounded-xl border border-outline-variant/10 p-6">
        <h2 className="text-sm font-mono text-outline uppercase tracking-widest mb-6">Brand Info</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Brand name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50"
              placeholder="Acme Corp"
            />
          </div>
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Website URL (optional)</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Industry</label>
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              maxLength={12}
              className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50"
              placeholder="SaaS"
            />
          </div>
          <button
            onClick={() => void handleSaveBrandInfo()}
            disabled={savingInfo}
            className="px-5 py-2 rounded-lg bg-primary/20 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/30 transition-colors disabled:opacity-50"
          >
            {savingInfo ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      {/* Prompts */}
      <section className="bg-surface-container rounded-xl border border-outline-variant/10 p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-mono text-outline uppercase tracking-widest">Prompts</h2>
          <span className="text-xs font-mono text-outline">{activePrompts.length} active</span>
        </div>
        <p className="text-xs text-on-surface-variant mb-5">
          Natural language questions sent to each AI model. Each prompt × model costs credits.
        </p>
        <div className="space-y-2 mb-4">
          {activePrompts.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-background border border-outline-variant/10">
              <span className="text-sm text-white leading-snug">{p.promptText}</span>
              <button
                onClick={() => void handleRemovePrompt(p.id)}
                className="text-outline hover:text-error transition-colors text-xs mt-0.5 shrink-0"
                title="Remove prompt"
              >
                ✕
              </button>
            </div>
          ))}
          {activePrompts.length === 0 && (
            <p className="text-sm text-outline py-2">No prompts yet.</p>
          )}
        </div>
        <div className="space-y-2">
          <textarea
            value={newPrompt}
            onChange={(e) => setNewPrompt(e.target.value)}
            rows={2}
            maxLength={PROMPT_MAX_LENGTH}
            className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50 resize-none"
            placeholder="e.g. What's the best tax firm in Idaho?"
          />
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs font-mono text-outline shrink-0">{newPrompt.length} / {PROMPT_MAX_LENGTH}</span>
              {newPrompt.trim().length > 0 && newPrompt.trim().length < 10 && (
                <span className="text-xs text-on-surface-variant truncate">
                  {10 - newPrompt.trim().length} more character{10 - newPrompt.trim().length !== 1 ? 's' : ''} needed
                </span>
              )}
            </div>
            <button
              onClick={() => void handleAddPrompt()}
              disabled={addingPrompt || newPrompt.trim().length < 10}
              className="px-5 py-2 rounded-lg bg-primary/20 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/30 transition-colors disabled:opacity-50 shrink-0"
            >
              {addingPrompt ? '…' : '+ Add Prompt'}
            </button>
          </div>
        </div>
      </section>

      {/* AI Models */}
      <section className="bg-surface-container rounded-xl border border-outline-variant/10 p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-mono text-outline uppercase tracking-widest">AI Models</h2>
        </div>
        <p className="text-xs text-on-surface-variant mb-5">
          Each selected model runs against all your prompts per check.
        </p>
        <div className="space-y-2 mb-4">
          {AI_MODELS.map((model) => {
            const enabled = ENABLED_MODELS.has(model)
            const checked = selectedModels.includes(model)
            const creditCost = MODEL_CREDIT_COST[model]
            return (
              <label
                key={model}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                  !enabled
                    ? 'border-outline-variant/5 opacity-40 cursor-not-allowed'
                    : checked
                      ? 'border-primary/40 bg-primary/5 cursor-pointer'
                      : 'border-outline-variant/10 hover:border-outline-variant/20 cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!enabled}
                  onChange={(e) => {
                    if (!enabled) return
                    if (e.target.checked) {
                      setSelectedModels((prev) => [...prev, model])
                    } else {
                      setSelectedModels((prev) => prev.filter((m) => m !== model))
                    }
                  }}
                  className="accent-primary"
                />
                <span className="text-sm text-white flex-1">{MODEL_LABELS[model]}</span>
                {!enabled && <span className="text-[10px] font-mono text-outline/50 mr-1">soon</span>}
                <span className="text-xs font-mono text-outline">{creditCost} cr</span>
              </label>
            )
          })}
        </div>
        <button
          onClick={() => void handleSaveModels()}
          disabled={savingModels || selectedModels.length === 0}
          className="px-5 py-2 rounded-lg bg-primary/20 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/30 transition-colors disabled:opacity-50"
        >
          {savingModels ? 'Saving…' : 'Save Models'}
        </button>
      </section>

      {/* Danger Zone */}
      <section className="bg-surface-container rounded-xl border border-error/20 p-6">
        <h2 className="text-sm font-mono text-error/70 uppercase tracking-widest mb-4">Danger Zone</h2>
        {!confirmDelete ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Delete this brand</p>
              <p className="text-xs text-on-surface-variant mt-0.5">Permanently removes all prompts, results, and jobs.</p>
            </div>
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-4 py-2 rounded-lg border border-error/30 text-error text-sm font-medium hover:bg-error/10 transition-colors"
            >
              Delete Brand
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-white">
              Delete <span className="font-semibold">{settings.brand.name}</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="flex-1 py-2 rounded-lg border border-outline-variant/30 text-white text-sm hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleDeleteBrand()}
                disabled={deleting}
                className="flex-1 py-2 rounded-lg bg-error/20 border border-error/40 text-error text-sm font-medium hover:bg-error/30 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
